import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Clock, ArrowLeft, Upload, FileText, Download, AlertCircle, CheckCircle, TrendingUp, TrendingDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export default function ControleDePonto() {
  const [file, setFile] = useState(null);
  const [data, setData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Constantes
  const HORA_ENTRADA_PADRAO = 8 * 60; // 08:00 em minutos
  const HORA_SAIDA_PADRAO = 17 * 60; // 17:00 em minutos
  const VALOR_HORA_NORMAL = 9.09;
  const VALOR_HORA_EXTRA = 13.63; // R$ 9.09 + 50% = R$ 13.63
  const VALOR_MINUTO_EXTRA = VALOR_HORA_EXTRA / 60;
  const VALOR_MINUTO_DESCONTO = VALOR_HORA_NORMAL / 60;
  const DESCONTO_POR_FALTA = 66.66;

  // Função para converter string de data/hora para minutos desde meia-noite
  const parseDateTime = (dateTimeStr) => {
    // Formato: "2025/09/01 07:17:12"
    const [datePart, timePart] = dateTimeStr.split(' ');
    const [year, month, day] = datePart.split('/');
    const [hour, minute, second] = timePart.split(':');
    
    const date = `${year}-${month}-${day}`;
    const minutesSinceMidnight = parseInt(hour) * 60 + parseInt(minute);
    
    return { date, minutesSinceMidnight, hour: parseInt(hour), minute: parseInt(minute) };
  };

  // Função para formatar minutos em HH:MM
  const formatMinutesToTime = (minutes) => {
    const hours = Math.floor(Math.abs(minutes) / 60);
    const mins = Math.abs(minutes) % 60;
    const sign = minutes < 0 ? '-' : '';
    return `${sign}${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  // Função para normalizar o CSV (remover quebras de linha indevidas)
  const normalizeCSV = (content) => {
    // Remove quebras de linha que estão no meio de uma linha de dados
    // O padrão é: linhas válidas começam com data (XX.XX) ou palavras-chave específicas
    let normalized = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    
    // Junta linhas que foram quebradas incorretamente
    // Uma linha válida de dados começa com padrão de data (11.01) ou é cabeçalho
    const lines = normalized.split('\n');
    const mergedLines = [];
    let currentLine = '';
    
    for (const line of lines) {
      const trimmedLine = line.trim();
      
      // Verifica se é início de uma nova linha válida
      const isNewDataLine = /^\d{2}\.\d{2};/.test(trimmedLine); // Linha de dados: 11.01;...
      const isHeaderLine = trimmedLine.startsWith('Data;Semana') || 
                          trimmedLine.startsWith(';;IN;OUT') ||
                          trimmedLine.startsWith('Dept:') ||
                          trimmedLine.startsWith('Plano:') ||
                          trimmedLine.startsWith('"Subsídios') ||
                          trimmedLine.startsWith(';;;;;;;;') ||
                          trimmedLine === ';;;;;;;;;;;;;;;' ||
                          /^;+$/.test(trimmedLine);
      
      if (isNewDataLine || isHeaderLine) {
        if (currentLine) {
          mergedLines.push(currentLine);
        }
        currentLine = trimmedLine;
      } else if (trimmedLine) {
        // Continua a linha anterior (remove espaços extras)
        currentLine += trimmedLine;
      }
    }
    
    if (currentLine) {
      mergedLines.push(currentLine);
    }
    
    console.log('=== CSV NORMALIZADO ===');
    console.log('Linhas após normalização:', mergedLines.length);
    mergedLines.forEach((line, i) => console.log(`Linha ${i}: ${line.substring(0, 100)}...`));
    
    return mergedLines.join('\n');
  };

  // Função para processar o arquivo CSV
  const processFile = (fileContent) => {
    setIsProcessing(true);
    setError(null);

    try {
      // Normaliza o CSV primeiro
      const normalizedContent = normalizeCSV(fileContent);
      const lines = normalizedContent.split('\n').filter(line => line.trim());
      
      // Encontrar o nome do funcionário e período no cabeçalho
      let funcionarioNome = 'N/A';
      let periodoInicio = null;
      let periodoFim = null;
      
      const headerLine = lines.find(line => line.includes('Nome:'));
      if (headerLine) {
        const matchNome = headerLine.match(/Nome:([^;]+)/);
        if (matchNome) {
          funcionarioNome = matchNome[1].trim();
        }
        
        // Extrair período do cabeçalho: Data:25.11.01~25.11.30
        const matchPeriodo = headerLine.match(/Data:(\d{2})\.(\d{2})\.(\d{2})~(\d{2})\.(\d{2})\.(\d{2})/);
        if (matchPeriodo) {
          const [, anoIni, mesIni, diaIni, anoFim, mesFim, diaFim] = matchPeriodo;
          // Converter para formato completo (assumindo século 20xx)
          periodoInicio = `01/${mesIni}/20${anoIni}`;
          periodoFim = `${diaFim}/${mesFim}/20${anoFim}`;
        }
      }
      
      // Encontrar a linha de cabeçalho das colunas (que contém "Data;Semana;Bom dia")
      const headerIndex = lines.findIndex(line => line.includes('Data;Semana;Bom dia'));
      if (headerIndex === -1) {
        throw new Error('Formato de arquivo inválido');
      }
      
      // Pular a linha de cabeçalho e a linha ";;IN;OUT;IN;OUT;IN;OUT"
      const dataLines = lines.slice(headerIndex + 2);
      
      // Processar cada linha
      const diasProcessados = [];
      const diasSemHorario = []; // Para feriados e faltas
      let totalMinutosExtras = 0;
      let totalMinutosDescontados = 0;
      let totalDescontoFaltas = 0;
      let totalFaltas = 0;
      
      console.log('=== INICIANDO PROCESSAMENTO DE LINHAS ===');
      console.log('Total de linhas de dados:', dataLines.length);
      
      dataLines.forEach((line, lineIndex) => {
        const parts = line.split(';');
        console.log(`\n--- Linha ${lineIndex + 1} ---`);
        console.log('Partes:', parts.length, parts);
        
        if (parts.length < 8) {
          console.log('Linha ignorada: menos de 8 partes');
          return;
        }
        
        // Processar duas datas por linha (colunas 0-7 e 8-15)
        // Estrutura: Data;Semana;IN;OUT;IN;OUT;IN;OUT
        const processarData = (offset) => {
          const dataStr = parts[offset]?.trim();
          const diaSemana = parts[offset + 1]?.trim();
          const entrada1 = parts[offset + 2]?.trim();
          // Estrutura: Data;Semana;IN(manhã);OUT(manhã);IN(tarde);OUT(tarde);IN(noite);OUT(noite)
          // A saída principal está em offset + 5 (OUT da tarde) - é onde normalmente registra a saída do dia
          // Se não houver, tenta offset + 3 (OUT da manhã)
          let saida1 = parts[offset + 5]?.trim();
          if (!saida1 || saida1 === '') {
            saida1 = parts[offset + 3]?.trim();
          }
          
          console.log(`  Processando offset ${offset}:`, { dataStr, diaSemana, entrada1, saida1 });
          
          if (!dataStr || !diaSemana) {
            console.log('  -> Ignorado: sem data ou dia da semana');
            return;
          }
          
          // Converter data de "10.01" para "2025-10-01"
          const [mes, dia] = dataStr.split('.');
          const ano = '2025'; // Assumir ano atual
          const dataFormatada = `${dia}/${mes}/${ano}`;
          const dataISO = `${ano}-${mes}-${dia}`;
          
          // Verificar se é feriado
          if (entrada1 === 'FERIADO' || saida1 === 'FERIADO') {
            console.log('  -> FERIADO detectado');
            diasSemHorario.push({
              data: dataISO,
              dataFormatada,
              tipo: 'FERIADO',
              diaSemana
            });
            return;
          }
          
          // Verificar se é falta
          if (entrada1 === 'FALTA' || saida1 === 'FALTA') {
            console.log('  -> FALTA detectada');
            diasSemHorario.push({
              data: dataISO,
              dataFormatada,
              tipo: 'FALTA',
              diaSemana
            });
            totalFaltas++;
            totalDescontoFaltas += DESCONTO_POR_FALTA;
            return;
          }
          
          // Verificar se é atestado médico (não desconta)
          if (entrada1 === 'ATESTADO' || saida1 === 'ATESTADO') {
            console.log('  -> ATESTADO detectado');
            diasSemHorario.push({
              data: dataISO,
              dataFormatada,
              tipo: 'ATESTADO',
              diaSemana
            });
            return;
          }
          
          // Remover asterisco e espaços
          const entradaLimpa = entrada1 ? entrada1.replace('*', '').trim() : '';
          const saidaLimpa = saida1 ? saida1.trim() : '';
          
          console.log('  Horários limpos:', { entradaLimpa, saidaLimpa });
          
          // Verificar se não tem horários válidos (fim de semana ou registro incompleto)
          const isHorarioValido = (h) => h && /^\d{1,2}:\d{2}$/.test(h);
          const entradaValida = isHorarioValido(entradaLimpa);
          const saidaValida = isHorarioValido(saidaLimpa);
          
          console.log('  Validação:', { entradaValida, saidaValida });
          
          if (!entradaValida || !saidaValida) {
            // Se tem entrada mas não tem saída, registrar como dia incompleto
            if (entradaValida && !saidaValida) {
              console.log('  -> INCOMPLETO: entrada válida mas saída inválida');
              diasSemHorario.push({
                data: dataISO,
                dataFormatada,
                tipo: 'INCOMPLETO',
                diaSemana,
                observacao: `Entrada: ${entradaLimpa}, sem saída registrada`
              });
            } else {
              console.log('  -> Ignorado: fim de semana ou sem horários');
            }
            return;
          }
          
          console.log('  -> DIA VÁLIDO: processando horas extras/descontos');
          
          // Converter horários para minutos
          const parseHora = (horaStr) => {
            const [hora, minuto] = horaStr.split(':').map(s => parseInt(s.trim()));
            return hora * 60 + minuto;
          };
          
          const entradaMinutos = parseHora(entradaLimpa);
          const saidaMinutos = parseHora(saidaLimpa);
          
          // Calcular diferenças
          let minutosExtras = 0;
          let minutosDescontados = 0;
          
          // Entrada antes das 08:00 = hora extra
          if (entradaMinutos < HORA_ENTRADA_PADRAO) {
            minutosExtras += HORA_ENTRADA_PADRAO - entradaMinutos;
          }
          // Entrada depois das 08:00 = desconto
          else if (entradaMinutos > HORA_ENTRADA_PADRAO) {
            minutosDescontados += entradaMinutos - HORA_ENTRADA_PADRAO;
          }
          
          // Saída depois das 17:00 = hora extra
          if (saidaMinutos > HORA_SAIDA_PADRAO) {
            minutosExtras += saidaMinutos - HORA_SAIDA_PADRAO;
          }
          
          // Calcular valores
          const valorExtras = minutosExtras * VALOR_MINUTO_EXTRA;
          const valorDescontos = minutosDescontados * VALOR_MINUTO_DESCONTO;
          const valorDia = valorExtras - valorDescontos;
          
          totalMinutosExtras += minutosExtras;
          totalMinutosDescontados += minutosDescontados;
          
          diasProcessados.push({
            data: dataISO,
            dataFormatada,
            nome: funcionarioNome,
            primeiraEntrada: entradaLimpa,
            ultimaSaida: saidaLimpa,
            minutosExtras,
            minutosDescontados,
            valorExtras,
            valorDescontos,
            valorDia,
            diaSemana
          });
        };
        
        // Processar primeira data (colunas 0-7)
        processarData(0);
        // Processar segunda data (colunas 8-15)
        if (parts.length > 8) {
          processarData(8);
        }
      });
      
      // Ordenar por data
      diasProcessados.sort((a, b) => a.data.localeCompare(b.data));
      diasSemHorario.sort((a, b) => a.data.localeCompare(b.data));
      
      console.log('\n=== RESUMO DO PROCESSAMENTO ===');
      console.log('Dias processados (com horário completo):', diasProcessados.length);
      console.log('Dias sem horário:', diasSemHorario.length);
      console.log('Detalhes dias processados:', diasProcessados);
      console.log('Detalhes dias sem horário:', diasSemHorario);
      
      // Calcular valores totais
      const valorTotalExtras = totalMinutosExtras * VALOR_MINUTO_EXTRA;
      const valorTotalDescontos = totalMinutosDescontados * VALOR_MINUTO_DESCONTO;
      const valorTotal = valorTotalExtras - valorTotalDescontos - totalDescontoFaltas;

      const resultado = {
        funcionario: funcionarioNome,
        periodo: {
          inicio: periodoInicio || diasProcessados[0]?.dataFormatada || 'N/A',
          fim: periodoFim || diasProcessados[diasProcessados.length - 1]?.dataFormatada || 'N/A'
        },
        totalDias: diasProcessados.length,
        totalMinutosExtras,
        totalMinutosDescontados,
        valorTotalExtras,
        valorTotalDescontos,
        totalFaltas,
        totalDescontoFaltas,
        valorTotal,
        dias: diasProcessados,
        diasSemHorario
      };

      setData(resultado);
    } catch (err) {
      setError(`Erro ao processar arquivo: ${err.message}`);
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handlers de upload
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (event) => {
        processFile(event.target.result);
      };
      reader.readAsText(selectedFile);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      const reader = new FileReader();
      reader.onload = (event) => {
        processFile(event.target.result);
      };
      reader.readAsText(droppedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  // Função para gerar PDF
  const generatePDF = () => {
    if (!data) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Título
    doc.setFontSize(20);
    doc.setTextColor(37, 99, 235); // blue-600
    doc.text('Relatório de Controle de Ponto', pageWidth / 2, 20, { align: 'center' });
    
    // Informações do Funcionário
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Funcionário: ${data.funcionario}`, 20, 35);
    doc.text(`Período: ${data.periodo.inicio} a ${data.periodo.fim}`, 20, 42);
    doc.text(`Total de dias trabalhados: ${data.totalDias}`, 20, 49);
    
    // Resumo Financeiro - Box colorido
    doc.setFillColor(239, 246, 255); // bg-blue-50
    doc.rect(20, 55, pageWidth - 40, 45, 'F');
    
    doc.setFontSize(14);
    doc.setTextColor(37, 99, 235);
    doc.text('Resumo Financeiro', 25, 63);
    
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(`Horas Extras: ${formatMinutesToTime(data.totalMinutosExtras)} = +R$ ${data.valorTotalExtras.toFixed(2).replace('.', ',')}`, 25, 71);
    doc.text(`Descontos Atraso: ${formatMinutesToTime(data.totalMinutosDescontados)} = -R$ ${data.valorTotalDescontos.toFixed(2).replace('.', ',')}`, 25, 78);
    doc.text(`Faltas: ${data.totalFaltas} dia(s) = -R$ ${data.totalDescontoFaltas.toFixed(2).replace('.', ',')}`, 25, 85);
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    const saldoTexto = data.valorTotal >= 0 ? 'Saldo Final Positivo' : 'Saldo Final Negativo';
    const valorTexto = `${data.valorTotal >= 0 ? '+' : ''}R$ ${data.valorTotal.toFixed(2).replace('.', ',')}`;
    doc.text(`${saldoTexto}: ${valorTexto}`, 25, 96);
    doc.setFont(undefined, 'normal');
    
    // Tabela de detalhamento diário
    const tableData = data.dias.map(dia => {
      return [
        dia.dataFormatada,
        dia.diaSemana,
        dia.primeiraEntrada,
        dia.ultimaSaida,
        formatMinutesToTime(dia.minutosExtras),
        formatMinutesToTime(dia.minutosDescontados),
        `R$ ${dia.valorDia.toFixed(2).replace('.', ',')}`
      ];
    });
    
    autoTable(doc, {
      startY: 105,
      head: [['Data', 'Dia', 'Entrada', 'Saída', 'H. Extras', 'Descontos', 'Valor']],
      body: tableData,
      theme: 'striped',
      headStyles: { 
        fillColor: [229, 231, 235], // gray-200
        textColor: [0, 0, 0],
        fontSize: 10,
        fontStyle: 'bold'
      },
      bodyStyles: { 
        fontSize: 9
      },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 18 },
        2: { cellWidth: 18 },
        3: { cellWidth: 18, textColor: [22, 163, 74], fontStyle: 'bold' }, // Saída sempre verde
        4: { cellWidth: 22, textColor: [22, 163, 74], fontStyle: 'bold' }, // green-600
        5: { cellWidth: 22, textColor: [220, 38, 38], fontStyle: 'bold' }, // red-600
        6: { cellWidth: 25, textColor: [37, 99, 235], fontStyle: 'bold' } // blue-600
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251] // gray-50
      },
      didParseCell: function(data) {
        // Colorir entrada: verde se antes das 08:00, vermelho se depois
        if (data.column.index === 2 && data.section === 'body') {
          const entrada = data.cell.text[0];
          if (entrada) {
            const [hora, minuto] = entrada.split(':').map(s => parseInt(s));
            const minutosDoDia = hora * 60 + minuto;
            if (minutosDoDia < 8 * 60) {
              data.cell.styles.textColor = [22, 163, 74]; // verde
            } else if (minutosDoDia > 8 * 60) {
              data.cell.styles.textColor = [220, 38, 38]; // vermelho
            }
          }
        }
      }
    });
    
    // Tabela de dias sem horário (feriados e faltas) - SEMPRE NA PÁGINA 2
    if (data.diasSemHorario && data.diasSemHorario.length > 0) {
      doc.addPage();
      
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, 'bold');
      doc.text('Dias sem Registro de Ponto', 20, 20);
      doc.setFont(undefined, 'normal');
      
      const diasSemHorarioData = data.diasSemHorario.map(dia => {
        return [
          dia.dataFormatada,
          dia.diaSemana,
          dia.tipo,
          dia.tipo === 'FALTA' ? `-R$ ${DESCONTO_POR_FALTA.toFixed(2)}` : 
          dia.tipo === 'ATESTADO' ? 'Justificado' : 
          dia.tipo === 'INCOMPLETO' ? 'Verificar' : '-'
        ];
      });
      
      autoTable(doc, {
        startY: 25,
        head: [['Data', 'Dia', 'Tipo', 'Desconto']],
        body: diasSemHorarioData,
        theme: 'striped',
        headStyles: { 
          fillColor: [229, 231, 235],
          textColor: [0, 0, 0],
          fontSize: 10,
          fontStyle: 'bold'
        },
        bodyStyles: { 
          fontSize: 9
        },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 25 },
          2: { cellWidth: 30 },
          3: { cellWidth: 30, textColor: [220, 38, 38], fontStyle: 'bold' }
        },
        alternateRowStyles: {
          fillColor: [249, 250, 251]
        }
      });
      
      const currentY = doc.lastAutoTable.finalY + 10;
      
      // Observações com sombreamento
      doc.setFillColor(239, 246, 255); // bg-blue-50
      doc.rect(20, currentY, pageWidth - 40, 30, 'F');
      
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text('Observações:', 25, currentY + 7);
      doc.text('- Horário padrão: 08:00 às 17:00', 25, currentY + 12);
      doc.text(`- Valor hora extra: R$ ${VALOR_HORA_EXTRA.toFixed(2).replace('.', ',')} (hora normal + 50%)`, 25, currentY + 17);
      doc.text(`- Desconto por falta: R$ ${DESCONTO_POR_FALTA.toFixed(2).replace('.', ',')}`, 25, currentY + 22);
      doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, 25, currentY + 27);
    } else {
      // Se não houver dias sem horário, adicionar observações na primeira página
      const currentY = doc.lastAutoTable.finalY + 10;
      
      // Observações com sombreamento
      doc.setFillColor(239, 246, 255); // bg-blue-50
      doc.rect(20, currentY, pageWidth - 40, 30, 'F');
      
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text('Observações:', 25, currentY + 7);
      doc.text('- Horário padrão: 08:00 às 17:00', 25, currentY + 12);
      doc.text(`- Valor hora extra: R$ ${VALOR_HORA_EXTRA.toFixed(2).replace('.', ',')} (hora normal + 50%)`, 25, currentY + 17);
      doc.text(`- Desconto por falta: R$ ${DESCONTO_POR_FALTA.toFixed(2).replace('.', ',')}`, 25, currentY + 22);
      doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, 25, currentY + 27);
    }
    
    // Salvar
    doc.save(`controle-ponto-${data.funcionario.replace(/\s+/g, '-')}.pdf`);
  };

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <header className="mb-8">
        <Link to={createPageUrl("Operations")}>
          <Button 
            variant="ghost" 
            className="mb-4 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para Operações
          </Button>
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <Clock className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Controle de Ponto</h1>
        </div>
        <p className="text-gray-500">
          Calcule horas extras e descontos com base nos registros de ponto
        </p>
      </header>

      {/* Área de Upload */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload de Arquivo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
                isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium text-gray-700 mb-2">
                Arraste o arquivo de ponto ou clique para selecionar
              </p>
              <p className="text-sm text-gray-500">
                Formato: arquivo .CSV com registros de ponto
              </p>
              {file && (
                <p className="mt-4 text-sm text-blue-600 font-medium">
                  Arquivo selecionado: {file.name}
                </p>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Erro */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-red-700">
                <AlertCircle className="w-5 h-5" />
                <p className="font-medium">{error}</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Resultados */}
      <AnimatePresence>
        {data && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
          >
            {/* Resumo */}
            <Card className="mb-6 border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Resumo do Período
                  </span>
                  <Button onClick={generatePDF} className="bg-blue-600 hover:bg-blue-700">
                    <Download className="w-4 h-4 mr-2" />
                    Baixar PDF
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Funcionário</p>
                    <p className="text-lg font-semibold">{data.funcionario}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Período</p>
                    <p className="text-lg font-semibold">{data.periodo.inicio} a {data.periodo.fim}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total de Dias</p>
                    <p className="text-lg font-semibold">{data.totalDias} dias</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      <p className="font-semibold text-green-900">Horas Extras</p>
                    </div>
                    <p className="text-2xl font-bold text-green-700">
                      {formatMinutesToTime(data.totalMinutosExtras)}
                    </p>
                    <p className="text-sm text-green-600 mt-1">
                      R$ {data.valorTotalExtras.toFixed(2).replace('.', ',')}
                    </p>
                  </div>

                  <div className="bg-red-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingDown className="w-5 h-5 text-red-600" />
                      <p className="font-semibold text-red-900">Descontos</p>
                    </div>
                    <p className="text-2xl font-bold text-red-700">
                      {formatMinutesToTime(data.totalMinutosDescontados)}
                    </p>
                    <p className="text-sm text-red-600 mt-1">
                      R$ {data.valorTotalDescontos.toFixed(2).replace('.', ',')}
                    </p>
                  </div>

                  <div className="bg-orange-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-5 h-5 text-orange-600" />
                      <p className="font-semibold text-orange-900">Faltas</p>
                    </div>
                    <p className="text-2xl font-bold text-orange-700">
                      {data.totalFaltas} dia{data.totalFaltas !== 1 ? 's' : ''}
                    </p>
                    <p className="text-sm text-orange-600 mt-1">
                      -R$ {data.totalDescontoFaltas.toFixed(2).replace('.', ',')}
                    </p>
                  </div>

                  <div className={`p-4 rounded-lg ${data.valorTotal >= 0 ? 'bg-blue-50' : 'bg-red-50'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className={`w-5 h-5 ${data.valorTotal >= 0 ? 'text-blue-600' : 'text-red-600'}`} />
                      <p className={`font-semibold ${data.valorTotal >= 0 ? 'text-blue-900' : 'text-red-900'}`}>
                        Saldo Final
                      </p>
                    </div>
                    <p className={`text-2xl font-bold ${data.valorTotal >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                      {data.valorTotal >= 0 ? '+' : ''}R$ {data.valorTotal.toFixed(2).replace('.', ',')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Detalhamento Diário */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Detalhamento Diário</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 border-b-2 border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">Data</th>
                        <th className="px-4 py-3 text-left font-semibold">Dia</th>
                        <th className="px-4 py-3 text-left font-semibold">Entrada</th>
                        <th className="px-4 py-3 text-left font-semibold">Saída</th>
                        <th className="px-4 py-3 text-left font-semibold">H. Extras</th>
                        <th className="px-4 py-3 text-left font-semibold">Descontos</th>
                        <th className="px-4 py-3 text-right font-semibold">Valor</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {data.dias.map((dia, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3">{dia.dataFormatada}</td>
                          <td className="px-4 py-3 text-gray-600">{dia.diaSemana}</td>
                          <td className="px-4 py-3">{dia.primeiraEntrada}</td>
                          <td className="px-4 py-3">{dia.ultimaSaida}</td>
                          <td className="px-4 py-3 text-green-600 font-medium">
                            {formatMinutesToTime(dia.minutosExtras)}
                            {dia.minutosExtras > 0 && (
                              <span className="text-xs block">+R$ {dia.valorExtras.toFixed(2)}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-red-600 font-medium">
                            {formatMinutesToTime(dia.minutosDescontados)}
                            {dia.minutosDescontados > 0 && (
                              <span className="text-xs block">-R$ {dia.valorDescontos.toFixed(2)}</span>
                            )}
                          </td>
                          <td className={`px-4 py-3 text-right font-semibold ${dia.valorDia >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                            {dia.valorDia >= 0 ? '+' : ''}R$ {dia.valorDia.toFixed(2).replace('.', ',')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Dias sem Horário (Feriados e Faltas) */}
            {data.diasSemHorario && data.diasSemHorario.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Dias sem Registro de Ponto</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 border-b-2 border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold">Data</th>
                          <th className="px-4 py-3 text-left font-semibold">Dia</th>
                          <th className="px-4 py-3 text-left font-semibold">Tipo</th>
                          <th className="px-4 py-3 text-right font-semibold">Desconto</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {data.diasSemHorario.map((dia, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3">{dia.dataFormatada}</td>
                            <td className="px-4 py-3 text-gray-600">{dia.diaSemana}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                dia.tipo === 'FALTA' ? 'bg-red-100 text-red-700' : 
                                dia.tipo === 'ATESTADO' ? 'bg-yellow-100 text-yellow-700' :
                                dia.tipo === 'INCOMPLETO' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                              }`}>
                                {dia.tipo}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-red-600">
                              {dia.tipo === 'FALTA' ? `-R$ ${DESCONTO_POR_FALTA.toFixed(2)}` : 
                               dia.tipo === 'ATESTADO' ? 'Justificado' : 
                               dia.tipo === 'INCOMPLETO' ? 'Verificar' : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
